import { isMobile } from "react-device-detect";
import styled from "styled-components";
import { ReactSVG } from "react-svg";
import { trackShopClick } from "../analytics";
import { GlobalStyleState, ProductState } from "../store/chart";
import { FontWeight, UIColor } from "../types";
import { PrimaryButton } from "./Button";
import { Tag } from "./Tag";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import Image from "./Image";

type ProductProps = {
  product: ProductState;
  styling: GlobalStyleState;
  iconColor?: UIColor;
  hoverIconColor?: UIColor;
};

type ProductHeaderProps = ProductProps & {
  hiddenName?: boolean;
  hiddenTag?: boolean;
};

export function ProductHeader({
  product,
  styling,
  hiddenName,
  hiddenTag,
}: ProductHeaderProps) {
  let HeaderComponent = Header;

  if (hiddenTag && hiddenName) {
    HeaderComponent = HeaderImageOnly;
  } else if (hiddenTag) {
    HeaderComponent = HeaderNoTag;
  } else if (hiddenName) {
    HeaderComponent = HeaderNoName;
  }

  return (
    <Container
      columnBackgroundColor={styling.column.backgroundColor?.hex}
      secondaryColumnBackgroundColor={
        product.isBundle ? styling.column.bundleBackgroundColor?.hex : ""
      }
    >
      <HeaderComponent>
        {!hiddenTag && (
          <ProductTagWrapper>
            {product.useTag && product.tag && (
              <ProductTag tag={product.tag} styling={styling} />
            )}
          </ProductTagWrapper>
        )}
        {!hiddenName && <ProductName>{product.name}</ProductName>}
        <ProductImageContainer>
          {product.isBundle ? (
            <ProductRelatedImageWrapper>
              <ProductRelatedImage>
                <ProductImage src={product.leftData.image} />
              </ProductRelatedImage>
              <ProductRelatedImage>
                <ProductImage src={product.rightData.image} />
              </ProductRelatedImage>
            </ProductRelatedImageWrapper>
          ) : (
            <ProductImageWrapper>
              <ProductImage src={product.data.image} alt={product.name} />
            </ProductImageWrapper>
          )}
        </ProductImageContainer>
      </HeaderComponent>
    </Container>
  );
}

type ProductBodyProps = ProductProps & {
  hiddenPrice?: boolean;
  hiddenShopButton?: boolean;
  hiddenSalesMessage?: boolean;
};

export function ProductBody({
  product,
  styling,
  hiddenPrice,
  hiddenShopButton,
  hiddenSalesMessage,
}: ProductBodyProps) {
  return (
    <BodyContainer
      columnBackgroundColor={styling.column.backgroundColor?.hex}
      secondaryColumnBackgroundColor={
        product.isBundle ? styling.column.bundleBackgroundColor?.hex : ""
      }
    >
      <Body hiddenPrice={hiddenPrice} hiddenSalesMessage={hiddenSalesMessage}>
        {!hiddenPrice && (
          <Pricing>
            {product.price && product.savePrice && (
              <>
                <ProductPrice textColor={styling.shopButton.backgroundColor}>
                  {product.savePrice}
                </ProductPrice>
                <ProductSave>{product.price}</ProductSave>
              </>
            )}
            {product.price && !product.savePrice && (
              <ProductPrice textColor={styling.shopButton.backgroundColor}>
                {product.price}
              </ProductPrice>
            )}
          </Pricing>
        )}
        {!hiddenSalesMessage && (
          <SalesMessage>
            {product.useSalesMessage && product.salesMessage && (
              <SalesMessageText
                textColor={product.salesMessage.color}
                font={product.salesMessage.font}
              >
                {product.salesMessage.value}
              </SalesMessageText>
            )}
          </SalesMessage>
        )}
        <ShopContainer>
          {!hiddenShopButton && product.shopUrl && (
            <ShopButton
              as="a"
              target="_blank"
              href={product.shopUrl}
              onClick={() => trackShopClick(product)}
              textColor={styling.shopButton.textColor}
              backgroundColor={styling.shopButton.backgroundColor}
              borderColor={styling.shopButton.borderColor}
              hover={styling.shopButton.hover}
            >
              {styling.shopButton.copy}
            </ShopButton>
          )}
          {/* {product.showShopSelectButton && product.shopSelectButton && (
            <>
              <ShopSelectSeparator />
              <ShopSelectDescription>
                {product.shopSelectButton.description}
              </ShopSelectDescription>
              <ShopButton
                as="a"
                target="_blank"
                href={product.shopSelectButton.url}
                onClick={() => trackShopSelectClick(product)}
                textColor={styling.shopSelectButton.textColor}
                backgroundColor={styling.shopSelectButton.backgroundColor}
                borderColor={styling.shopSelectButton.borderColor}
                hover={styling.shopSelectButton.hover}
              >
                {product.shopSelectButton.buttonCopy}
              </ShopButton>
            </>
          )} */}
        </ShopContainer>
      </Body>
    </BodyContainer>
  );
}

export function ProductFooter({ product, styling }: ProductProps) {
  return (
    <FooterContainer
      columnBackgroundColor={styling.column.backgroundColor?.hex}
      secondaryColumnBackgroundColor={
        product.isBundle ? styling.column.bundleBackgroundColor?.hex : ""
      }
    >
      <Footer>
        <Application
          font={styling.productApplication?.font}
          textColor={styling.productApplication?.textColor}
        >
          {product.application}
        </Application>
      </Footer>
    </FooterContainer>
  );
}

export function ProductBestFor({
  product,
  styling,
  iconColor,
  hoverIconColor,
}: ProductProps) {
  return (
    <FooterContainer
      columnBackgroundColor={styling.column.backgroundColor?.hex}
      secondaryColumnBackgroundColor={
        product.isBundle ? styling.column.bundleBackgroundColor?.hex : ""
      }
    >
      <Footer>
        {product.bestForIcons && product.bestForIcons?.length > 0 && (
          <BestForIconList>
            {product.bestForIcons.map((bestForIcon) => (
              <Tooltip
                label={bestForIcon.alt || ""}
                size="sm"
                placement="top"
                event="hover"
                key={bestForIcon.alt}
                hideCloseButton
              >
                <BestForIconButton
                  iconColor={iconColor}
                  hoverIconColor={hoverIconColor}
                >
                  <ReactSVG src={bestForIcon.url} />
                </BestForIconButton>
              </Tooltip>
            ))}
          </BestForIconList>
        )}
      </Footer>
    </FooterContainer>
  );
}

const Container = styled.div<{
  columnBackgroundColor?: string;
  secondaryColumnBackgroundColor?: string;
}>`
  position: relative;
  z-index: 1;
  background: ${({ theme, columnBackgroundColor = theme.colors.white }) =>
    columnBackgroundColor};
  border-radius: ${({ theme }) =>
    `${theme.getValue(10)}px ${theme.getValue(10)}px 0 0`};

  &:before {
    position: absolute;
    z-index: -1;
    top: 0;
    left: 50%;
    width: 50%;
    height: 100%;
    content: "";
    border-radius: ${({ theme }) => `0 ${theme.getValue(10)}px 0 0`};
    background: ${({ theme, secondaryColumnBackgroundColor }) =>
      secondaryColumnBackgroundColor
        ? theme.isMobile
          ? `linear-gradient(transparent 100px, ${secondaryColumnBackgroundColor} 124px)`
          : secondaryColumnBackgroundColor
        : "none"};
  }
`;

const BodyContainer = styled(Container)`
  margin-top: 1px;
  height: calc(100% - 1px);
  border-radius: 0;

  &:before {
    border-radius: 0;
    background: ${({ secondaryColumnBackgroundColor }) =>
      secondaryColumnBackgroundColor ? secondaryColumnBackgroundColor : "none"};
  }
`;

const FooterContainer = styled(BodyContainer)`
  margin-top: ${({ theme }) => (theme.isMobile ? 0 : 1)}px;
`;

const Header = styled.div`
  display: grid;
  justify-items: center;
  grid-template-rows: ${({ theme }) =>
    theme.isMobile
      ? `${theme.getValue(24)}px ${theme.getValue(48)}px ${theme.getValue(
          80,
        )}px`
      : `${theme.getValue(38)}px ${theme.getValue(100)}px ${theme.getValue(
          48,
        )}px`};
  row-gap: ${({ theme }) =>
    theme.isMobile ? theme.getValue(12) : theme.getValue(20)}px;
  padding: ${({ theme }) =>
    theme.isMobile
      ? `${theme.getValue(6)}px`
      : `${theme.getValue(16)}px ${theme.getValue(14)}px`};
`;

const HeaderNoTag = styled(Header)`
  grid-template-rows: ${({ theme }) =>
    theme.isMobile
      ? `${theme.getValue(48)}px ${theme.getValue(100)}px`
      : `${theme.getValue(100)}px ${theme.getValue(48)}px`};
`;

const HeaderNoName = styled(Header)`
  grid-template-rows: ${({ theme }) =>
    theme.isMobile
      ? `${theme.getValue(24)}px ${theme.getValue(100)}px`
      : `${theme.getValue(38)}px ${theme.getValue(100)}px`};
`;

const HeaderImageOnly = styled(Header)`
  grid-template-rows: ${({ theme }) => `${theme.getValue(100)}px`};
`;

const Body = styled.div<{
  hiddenPrice?: boolean;
  hiddenSalesMessage?: boolean;
}>`
  display: grid;
  grid-template-rows: ${({ theme, hiddenPrice, hiddenSalesMessage }) => {
    const priceHeight = theme.isMobile
      ? `${theme.getValue(14)}px`
      : `${theme.getValue(24)}px`;
    const salesMessageHeight = theme.isMobile
      ? `${theme.getValue(14)}px`
      : `${theme.getValue(20)}px`;
    const rows = [];
    if (!hiddenPrice) {
      rows.push(priceHeight);
    }
    if (!hiddenSalesMessage) {
      rows.push(salesMessageHeight);
    }
    if (hiddenPrice && hiddenSalesMessage) {
      rows.push("1fr");
    }
    return rows.join(" ");
  }};
  grid-auto-rows: 1fr;
  row-gap: ${({ theme, hiddenPrice, hiddenSalesMessage }) => {
    if (hiddenPrice && hiddenSalesMessage) {
      return "0px";
    }
    if (isMobile) {
      return `${theme.getValue(8)}px`;
    }
    return `${theme.getValue(16)}px`;
  }};
  padding: ${({ theme }) =>
    theme.isMobile
      ? `${theme.getValue(12)}px ${theme.getValue(6)}px`
      : `${theme.getValue(18)}px ${theme.getValue(14)}px`};
  justify-items: center;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: ${({ theme }) =>
    theme.isMobile
      ? `${theme.getValue(10)}px`
      : `${theme.getValue(32)}px ${theme.getValue(16)}px`};
`;

const ProductTagWrapper = styled.div`
  order: 1;
  min-width: ${({ theme }) => (theme.isMobile ? "100%" : `auto`)};
`;

const ProductTag = styled(Tag)`
  display: ${({ theme }) => (theme.isMobile ? "block" : "inline-block")};
  width: ${({ theme }) => (theme.isMobile ? "100%" : "auto")};
  min-width: ${({ theme }) =>
    theme.isMobile ? "100%" : `${theme.getValue(167)}px`};
`;

const ProductName = styled(Text)`
  &&& {
    font-weight: ${({ theme }) =>
      theme.isMobile ? FontWeight.Normal : FontWeight.Bold};
    font-size: ${({ theme }) =>
      theme.isMobile ? theme.getFontSize(16) : theme.getFontSize(22)}px;
    line-height: 1;
    display: flex;
    align-items: center;
    text-align: center;
    margin: 0;
    order: ${({ theme }) => (theme.isMobile ? 2 : 3)};
  }
`;

const ProductImageContainer = styled.div`
  display: flex;
  align-items: center;
  order: ${({ theme }) => (theme.isMobile ? 3 : 2)};
`;

const ProductImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  width: ${({ theme }) =>
    theme.isMobile ? theme.getValue(80) : theme.getValue(110)}px;
  height: ${({ theme }) =>
    theme.isMobile ? theme.getValue(80) : theme.getValue(110)}px;
`;

const ProductRelatedImageWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${({ theme }) => theme.getValue(100)}px;
  margin: 0 auto;
  order: ${({ theme }) => (theme.isMobile ? 3 : 2)};
`;

const ProductRelatedImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  width: ${({ theme }) =>
    theme.isMobile ? theme.getValue(50) : theme.getValue(104)}px;
  height: ${({ theme }) =>
    theme.isMobile ? theme.getValue(50) : theme.getValue(104)}px;
`;

const ProductImage = styled(Image).attrs(({ responsiveWidth }) => ({
  responsiveWidth: responsiveWidth || 160,
}))`
  display: block;
  width: 100%;
  height: auto;
  max-width: 100%;
  max-height: 100%;
`;

const Pricing = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) =>
    theme.isMobile ? `${theme.getValue(4)}px` : `${theme.getValue(10)}px`};
`;

const SalesMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SalesMessageText = styled(Text)`
  &&& {
    font-weight: ${({ font: { weight = FontWeight.Bold } = {} }) => weight};
    font-size: ${({ theme }) =>
      theme.isMobile ? theme.getFontSize(12) : theme.getFontSize(18)}px;
    line-height: 1.14;
    margin: 0;
  }
`;

const ProductPrice = styled(Text)`
  &&& {
    font-weight: ${({ font: { weight = FontWeight.Bold } = {} }) => weight};
    font-size: ${({ theme }) =>
      theme.isMobile ? theme.getFontSize(12) : theme.getFontSize(18)}px;
    line-height: 1;
    margin: 0;
    text-align: center;
    color: ${({ theme, textColor }) => textColor?.hex || theme.colors.green2};
  }
`;

const ProductSave = styled(Text)`
  &&& {
    font-weight: ${({ font: { weight = FontWeight.Bold } = {} }) => weight};
    font-size: ${({ theme }) =>
      theme.isMobile ? theme.getFontSize(10) : theme.getFontSize(16)}px;
    line-height: 1;
    margin: 0;
    text-align: center;
    text-decoration: line-through;
    opacity: 0.4;
  }
`;

const ShopContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const ShopButton = styled(PrimaryButton)`
  display: ${({ theme }) => (theme.isMobile ? "block" : "inline-block")};
  width: ${({ theme }) => (theme.isMobile ? "100%" : "auto")};
  min-width: ${({ theme }) =>
    theme.isMobile ? "100%" : `${theme.getValue(155)}px`};
  text-decoration: none;

  &:visited {
    color: ${({ theme, textColor }) => textColor?.hex || theme.colors.white};
  }
`;

const Application = styled(Text)`
  &&& {
    font-weight: ${({ theme }) =>
      theme.isMobile ? FontWeight.Normal : FontWeight.SemiBold};
    font-size: ${({ theme }) =>
      theme.isMobile ? theme.getFontSize(16) : theme.getFontSize(22)}px;
    line-height: 1;
    margin: 0;
    text-align: center;
  }
`;

const BestForIconList = styled.div`
  display: flex;
  gap: ${({ theme }) =>
    theme.isMobile ? theme.getValue(2) : theme.getValue(8)}px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
`;

const BestForIconButton = styled.button<{
  iconColor?: UIColor;
  hoverIconColor?: UIColor;
}>`
  margin: 0;
  padding: 0;
  cursor: pointer;
  border: 0;
  background: none;
  appearance: none;

  img,
  svg {
    display: block;
    width: ${({ theme }) =>
      theme.isMobile ? theme.getValue(30) : theme.getValue(64)}px;
    height: auto;

    path {
      fill: ${({ iconColor, theme }) => iconColor?.hex || theme.colors.black};
      transition: all 0.2s;
    }
  }

  &:hover {
    path {
      fill: ${({ hoverIconColor, theme }) =>
        hoverIconColor?.hex || theme.colors.green1};
    }
  }
`;
